import React, { useState } from 'react';
import NoDataFallback from '../NoDataFallback/NoDataFallback';

export interface TableFilter {
  key: string;
  label: string;
  filter: (item: any) => boolean;
}

export interface GenericMetricsTableProps<T> {
  title: string;
  data: T[];
  filters?: TableFilter[];
  className?: string;
  renderHeader: () => React.ReactNode;
  renderRow: (item: T, index: number) => React.ReactNode;
  keyExtractor: (item: T, index: number) => string | number;
  isLoading?: boolean;
  getRowClassName?: (item: T, index: number) => string;
}

interface TableFiltersProps {
  filters: TableFilter[];
  activeFilter: string;
  onFilterChange: (filterKey: string) => void;
}

function TableFilters({ filters, activeFilter, onFilterChange }: TableFiltersProps) {
  return (
    <div className="generic-metrics-table__filters">
      {filters.map((filter) => (
        <button
          key={filter.key}
          className={`generic-metrics-table__filter-button ${
            activeFilter === filter.key ? 'generic-metrics-table__filter-button--active' : ''
          }`}
          onClick={() => onFilterChange(filter.key)}
          type="button">
          {filter.label}
        </button>
      ))}
    </div>
  );
}

export default function GenericMetricsTable<T>({
  title,
  data,
  filters = [],
  className = '',
  renderHeader,
  renderRow,
  keyExtractor,
  isLoading,
  getRowClassName,
}: GenericMetricsTableProps<T>) {
  const [activeFilter, setActiveFilter] = useState<string>(filters[0]?.key || 'all');
  const filteredData = React.useMemo(() => {
    if (isLoading) return ['', '', '']; // returning empty rows for skeleton rendering
    if (!filters.length) return data;

    const currentFilter = filters.find((f) => f.key === activeFilter);
    if (!currentFilter) return data;

    return data.filter(currentFilter.filter);
  }, [data, filters, activeFilter]);

  return (
    <div className={`generic-metrics-table ${className}`}>
      <div className="generic-metrics-table__header">
        <h3 className="generic-metrics-table__title">{title}</h3>
        {filters.length > 0 && (
          <TableFilters filters={filters} activeFilter={activeFilter} onFilterChange={setActiveFilter} />
        )}
      </div>

      <div className="generic-metrics-table__container">
        <div className="generic-metrics-table__head">{renderHeader()}</div>

        <div className="generic-metrics-table__body">
          {filteredData.map((item, index) => (
            <div
              key={keyExtractor(item, index)}
              className={`generic-metrics-table__row ${getRowClassName ? getRowClassName(item, index) : ''}`}>
              {renderRow(item, index)}
            </div>
          ))}
          <NoDataFallback show={!data?.length && !isLoading} />
        </div>
      </div>
    </div>
  );
}
